import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  Users,
  TrendingUp,
  Heart,
  Star,
  ArrowRight,
  Send,
  CheckCircle,
  Coffee,
  Zap,
  Globe,
  Award,
  Lightbulb,
  Shield,
  Target,
  BookOpen,
  Laptop
} from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';

const CareersPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    resume: null,
    coverLetter: '',
    position: '',
    experience: '',
    availability: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const departments = [
    { id: 'all', name: 'All Positions', count: 3 },
    { id: 'engineering', name: 'Engineering', count: 1 },
    { id: 'product', name: 'Product', count: 1 },
    { id: 'design', name: 'Design', count: 1 }
  ];

  const benefits = [
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Competitive Compensation',
      description: 'Industry-leading salary packages with equity options and performance bonuses.',
      color: 'from-green-400 to-emerald-500'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, dental, vision, and mental health support.',
      color: 'from-red-400 to-pink-500'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Flexible Work',
      description: 'Remote-first culture with flexible hours and unlimited PTO policy.',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Learning & Growth',
      description: '5,000 INR annual learning budget for courses, conferences, and certifications.',
      color: 'from-purple-400 to-indigo-500'
    },
    {
      icon: <Laptop className="w-8 h-8" />,
      title: 'Top-Tier Equipment',
      description: 'Latest MacBooks, multiple monitors, and any tools you need to excel.',
      color: 'from-orange-400 to-red-500'
    },
    {
      icon: <Coffee className="w-8 h-8" />,
      title: 'Office Perks',
      description: 'Catered meals, premium coffee, game rooms, and team building events.',
      color: 'from-yellow-400 to-orange-500'
    }
  ];

  const jobOpenings = [
    {
      id: 1,
      title: 'Senior Machine Learning Engineer',
      department: 'engineering',
      location: 'Remote Only',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Join our AI team to build next-generation machine learning systems that power intelligent applications for enterprise clients.',
      requirements: [
        '5+ years of experience in machine learning and AI',
        'Proficiency in Python, TensorFlow/PyTorch, and cloud platforms',
        'Experience with large-scale ML systems and model deployment',
        'Strong understanding of deep learning architectures',
        'Experience with MLOps and model lifecycle management'
      ],
      responsibilities: [
        'Design and implement ML models for production systems',
        'Optimize model performance and scalability',
        'Collaborate with product teams to define AI features',
        'Mentor junior engineers and establish best practices',
        'Research and evaluate new AI techniques and frameworks'
      ],
      tags: ['Python', 'Machine Learning', 'TensorFlow', 'AWS', 'Kubernetes'],
      urgent: true
    },
    {
      id: 5,
      title: 'Product Manager - AI Solutions',
      department: 'product',
      location: 'Remote Only',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Drive product strategy and roadmap for AI-powered solutions, working closely with engineering and client teams.',
      requirements: [
        '5+ years of product management experience in tech',
        'Deep understanding of AI/ML technologies and applications',
        'Experience with B2B SaaS products and enterprise clients',
        'Strong analytical skills and data-driven decision making',
        'Excellent communication and stakeholder management skills'
      ],
      responsibilities: [
        'Define product vision and strategy for AI solutions',
        'Collaborate with engineering teams to prioritize features',
        'Conduct market research and competitive analysis',
        'Manage product roadmap and communicate with stakeholders',
        'Work with sales team to understand client requirements'
      ],
      tags: ['Product Strategy', 'AI/ML', 'B2B SaaS', 'Analytics', 'Roadmap'],
      urgent: false
    },
    {
      id: 6,
      title: 'UX/UI Designer',
      department: 'design',
      location: 'Remote Only',
      type: 'Full-time',
      experience: '4+ years',
      description: 'Create intuitive and beautiful interfaces for complex AI applications, making advanced technology accessible to users.',
      requirements: [
        '4+ years of UX/UI design experience',
        'Proficiency in Figma, Sketch, and design systems',
        'Experience designing for B2B SaaS applications',
        'Understanding of user research and usability testing',
        'Portfolio demonstrating complex interface design'
      ],
      responsibilities: [
        'Design user interfaces for AI-powered applications',
        'Conduct user research and usability testing',
        'Create and maintain design systems and component libraries',
        'Collaborate with product and engineering teams',
        'Prototype and iterate on design concepts'
      ],
      tags: ['UX Design', 'UI Design', 'Figma', 'Design Systems', 'User Research'],
      urgent: false
    }
  ];

  const companyValues = [
    {
      icon: <Lightbulb className="w-12 h-12" />,
      title: 'Innovation First',
      description: 'We push boundaries and challenge conventional thinking to create breakthrough solutions.',
      color: 'from-yellow-400 to-orange-500'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Collaborative Spirit',
      description: 'We believe the best solutions emerge from diverse perspectives working together.',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: 'Results Driven',
      description: 'We focus on delivering measurable impact for our clients and stakeholders.',
      color: 'from-green-400 to-teal-500'
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: 'People First',
      description: 'We prioritize the growth, well-being, and success of our team members.',
      color: 'from-red-400 to-pink-500'
    }
  ];

  const filteredJobs = selectedDepartment === 'all'
    ? jobOpenings
    : jobOpenings.filter(job => job.department === selectedDepartment);

  const handleApplicationSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');

    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success');
      setApplicationForm({
        name: '', email: '', phone: '', resume: null,
        coverLetter: '', position: '', experience: '', availability: ''
      });
      setSelectedJob(null);
      setTimeout(() => setFormStatus(''), 5000);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEO
        title="Careers at Qivra Technologies - Join Our AI & IT Team | Remote & On-site Jobs"
        description="Join Qivra Technologies' innovative AI and IT team. Intelligence unleashed through passionate professionals. Explore remote and on-site career opportunities."
        keywords="AI careers, IT jobs, machine learning engineer, software developer jobs, remote work, tech careers, Qivra Technologies careers"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle, rgba(14, 165, 233, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 100% 100%, 60px 60px'
            }}
          />

          {/* Floating Logo Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Top Area */}
            <motion.div
              className="absolute top-20 left-16 opacity-7"
              animate={{
                y: [0, -26, 0],
                x: [0, 18, 0],
                rotate: [0, 4, 0]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-28 right-12 opacity-5"
              animate={{
                y: [0, -22, 0],
                x: [0, -14, 0],
                rotate: [0, -6, 0]
              }}
              transition={{
                duration: 16,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            {/* Middle Area */}
            <motion.div
              className="absolute top-1/2 left-6 opacity-4"
              animate={{
                y: [0, -34, 0],
                x: [0, 26, 0],
                rotate: [0, 9, 0]
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            {/* Bottom Area */}
            <motion.div
              className="absolute bottom-32 left-10 opacity-8"
              animate={{
                y: [0, -32, 0],
                x: [0, 24, 0],
                rotate: [0, 9, 0]
              }}
              transition={{
                duration: 14,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 6
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-20 right-20 opacity-6"
              animate={{
                y: [0, -36, 0],
                x: [0, -20, 0],
                rotate: [0, -7, 0]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 8
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Shape the Future</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
              <span className="text-white">Join Our</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light">
                Team
              </span>
            </h1>

            <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
              Intelligence unleashed through passionate professionals building the future of AI and technology
            </p>
          </motion.div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Open Positions
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              We're actively hiring talented individuals across multiple departments.
              Find your perfect role and join our mission.
            </p>

            {/* Department Filter */}
            <div className="flex flex-wrap justify-center gap-4">
              {departments.map((dept) => (
                <motion.button
                  key={dept.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedDepartment(dept.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${selectedDepartment === dept.id
                    ? 'bg-gradient-to-r from-cyan-400 to-purple-600 text-white shadow-lg shadow-cyan-400/50'
                    : 'bg-gray-800/50 text-gray-300 hover:text-cyan-400 border border-gray-700 hover:border-cyan-400/50'
                    }`}
                >
                  <span>{dept.name}</span>
                  <span className="bg-white/20 px-2 py-1 rounded-full text-xs">{dept.count}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Job Listings */}
          <div className="space-y-6">
            {filteredJobs.map((job, idx) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                onClick={() => setSelectedJob(job)}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-3xl border border-gray-800 hover:border-cyan-400/50 transition-all cursor-pointer group relative overflow-hidden"
              >
                {job.urgent && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-bl-2xl text-sm font-bold">
                    🔥 Urgent Hiring
                  </div>
                )}

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex-grow">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-4">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{job.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{job.type}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            <span>{job.experience}</span>
                          </div>

                        </div>
                      </div>
                    </div>

                    <p className="text-gray-300 mb-4 leading-relaxed">
                      {job.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {job.tags.map((tag, tagIdx) => (
                        <span
                          key={tagIdx}
                          className="bg-cyan-500/20 text-cyan-300 px-3 py-1 rounded-full text-sm border border-cyan-400/30"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gradient-to-r from-cyan-400 to-purple-600 px-6 py-3 rounded-full font-bold text-white hover:shadow-lg hover:shadow-cyan-400/50 transition-all flex items-center gap-2"
                    >
                      Apply Now
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits & Culture */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-600 bg-clip-text text-transparent">
                Why Join Qivra?
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We offer more than just a job – we provide an environment where you can grow,
              innovate, and make a meaningful impact on the future of technology.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl border border-gray-700 hover:border-cyan-400/50 transition-all text-center"
              >
                <div className={`text-cyan-400 mb-6 flex justify-center`}>
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{benefit.title}</h3>
                <p className="text-gray-400 leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Company Values */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-black text-white mb-8">Our Core Values</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyValues.map((value, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className={`text-cyan-400 mb-4 flex justify-center`}>
                    {value.icon}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3">{value.title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Job Application Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-cyan-400/30 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-black text-white mb-2">{selectedJob.title}</h2>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {selectedJob.location}
                    </span>

                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-400 hover:text-white text-2xl font-bold"
                >
                  ✕
                </button>
              </div>

              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-white mb-4">Job Description</h3>
                    <p className="text-gray-300 leading-relaxed">{selectedJob.description}</p>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-white mb-4">Requirements</h3>
                    <ul className="space-y-2">
                      {selectedJob.requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-300">
                          <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Key Responsibilities</h3>
                    <ul className="space-y-2">
                      {selectedJob.responsibilities.map((resp, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-gray-300">
                          <Target className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Application Form */}
                <div className="bg-gradient-to-br from-cyan-500/10 to-purple-600/10 p-6 rounded-2xl border border-cyan-400/30">
                  <h3 className="text-xl font-bold text-white mb-6">Apply for this Position</h3>

                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                      <h4 className="text-xl font-bold text-white mb-2">Application Submitted!</h4>
                      <p className="text-gray-300">Thank you for your interest. We'll be in touch soon.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleApplicationSubmit} className="space-y-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Full Name *"
                          value={applicationForm.name}
                          onChange={(e) => setApplicationForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all"
                          required
                        />
                      </div>

                      <div>
                        <input
                          type="email"
                          placeholder="Email Address *"
                          value={applicationForm.email}
                          onChange={(e) => setApplicationForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all"
                          required
                        />
                      </div>

                      <div>
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          value={applicationForm.phone}
                          onChange={(e) => setApplicationForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-white font-semibold mb-2">Resume *</label>
                        <div className="border-2 border-dashed border-gray-600 rounded-xl p-6 text-center hover:border-cyan-400 transition-colors">
                          <div className="text-gray-400 mb-2">
                            <Briefcase className="w-8 h-8 mx-auto mb-2" />
                            Drop your resume here or click to browse
                          </div>
                          <input type="file" className="hidden" accept=".pdf,.doc,.docx" />
                        </div>
                      </div>

                      <div>
                        <textarea
                          placeholder="Cover Letter / Why are you interested in this role?"
                          value={applicationForm.coverLetter}
                          onChange={(e) => setApplicationForm(prev => ({ ...prev, coverLetter: e.target.value }))}
                          rows={4}
                          className="w-full px-4 py-3 bg-gray-800 border-2 border-gray-700 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none transition-all resize-none"
                        />
                      </div>

                      <motion.button
                        type="submit"
                        disabled={formStatus === 'sending'}
                        whileHover={{ scale: formStatus === 'sending' ? 1 : 1.02 }}
                        whileTap={{ scale: formStatus === 'sending' ? 1 : 0.98 }}
                        className={`w-full bg-gradient-to-r from-cyan-400 to-purple-600 text-white px-6 py-4 rounded-full font-bold hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center justify-center gap-2 ${formStatus === 'sending' ? 'opacity-75 cursor-not-allowed' : ''
                          }`}
                      >
                        {formStatus === 'sending' ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting Application...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Submit Application
                          </>
                        )}
                      </motion.button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-500/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              Don't See the Perfect Role?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              We're always looking for exceptional talent. Send us your resume and let's
              explore how you can contribute to our mission.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-400 to-purple-600 px-8 py-4 rounded-full font-bold text-white hover:shadow-xl hover:shadow-cyan-400/50 transition-all flex items-center gap-2 justify-center"
              >
                Send Open Application
                <Send className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-cyan-400 text-cyan-400 px-8 py-4 rounded-full font-bold hover:bg-cyan-400 hover:text-black transition-all"
              >
                Join Our Talent Network
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;